<template>
    <div>
      <p>{{ t('plantCountCalculatorDescription') }}</p>
      <hr />
      <div class="row">
        <div class="col-sm">
          <GenericInput
            :input-label="t('growSpaceWidth')"
            v-model.number="growSpaceWidthFt"
            :conversion-factors="ConversionFactors.basicDistance"
          />
          <GenericInput
            :input-label="t('growSpaceLength')"
            v-model.number="growSpaceLengthFt"
            :conversion-factors="ConversionFactors.basicDistance"
          />
          <GenericInput
            :input-label="t('plantWidth')"
            v-model.number="plantWidthFt"
            :conversion-factors="ConversionFactors.basicDistance"
          />
          <GenericInput
            :input-label="t('plantLength')"
            v-model.number="plantLengthFt"
            :conversion-factors="ConversionFactors.basicDistance"
          />
        </div>
        <div class="col-sm">
            <FixedUnitOutput
            :output-label="t('gridLayout')"
            :number="plantLayout.plantsWide"
            :unit="t('plantsWideBy')"
            />
            <FixedUnitOutput
            noColon
            :number="plantLayout.plantsLong"
            :unit="t('plantsLong')"
            />
            <FixedUnitOutput
            :output-label="t('with')"
            :number="plantLayout.crackFill"
            :unit="t('plantsInRemainingSpace')"
            />
            <FixedUnitOutput
            :output-label="t('totalPlants')"
            :number="totalPlants"
            :unit="t('plants')"
            />
            <hr />
            <FixedUnitOutput
            :output-label="t('roomArea')"
            :number="totalSpace"
            :unit="t('squareFeet')"
            />
            <FixedUnitOutput
            :output-label="t('usedArea')"
            :number="usedSpace"
            :unit="t('squareFeet')"
            />
            <FixedUnitOutput
            :output-label="t('wastedSpace')"
            :number="wastedSpace"
            :unit="t('squareFeet')"
            />
            <FixedUnitOutput
            :output-label="t('spaceEfficiency')"
            :number="spaceEfficiency"
            :unit="t('percent')"
            />
        </div>
      </div>
      <i>{{ t('plantCountSpacingNote') }}</i>
    </div>
  </template>

  
  <script>
  import { ref, computed, reactive } from 'vue';
  import GenericInput from '../inputs/GenericInput.vue';
  import FixedUnitOutput from '../inputs/FixedUnitOutput.vue';
  import ConversionFactors from '../../utils/conversion_factors';
  import { round } from '../../utils/math';
  import { useI18n } from 'vue-i18n';
  
  export default {
    components: {
      GenericInput,
      FixedUnitOutput,
    },
    setup() {
        const growSpaceWidthFt = ref(10);
        const growSpaceLengthFt = ref(7);
        const plantWidthFt = ref(3);
        const plantLengthFt = ref(1);

        const { t} = useI18n();

        // Create a reactive object with a function
        const translations = reactive({
          t: (key) => t(key)
        })

  
        const simpleMaxLayout = (areaWidth, areaLength, objWidth, objLength) => {
            const stdWCount = Math.floor(areaWidth / objWidth);
            const stdLCount = Math.floor(areaLength / objLength);

            const swapWCount = Math.floor(areaWidth / objLength);
            const swapLCount = Math.floor(areaLength / objWidth);

            return stdWCount * stdLCount > swapWCount * swapLCount
                    ? stdWCount * stdLCount
                    : swapWCount * swapLCount;
        };

        const plantLayout = computed(() => {
            // Naive layout calculations
            const resultsStd = {
                plantsLong: Math.floor(growSpaceLengthFt.value / plantLengthFt.value),
                plantsWide: Math.floor(growSpaceWidthFt.value / plantWidthFt.value),
            };

            

            // Crackfilling calculations for standard layout
            const crackFillStdWide = simpleMaxLayout(
                growSpaceWidthFt.value - plantWidthFt.value * resultsStd.plantsWide,
                growSpaceLengthFt.value,
                plantWidthFt.value,
                plantLengthFt.value
            );
            const crackFillStdLong = simpleMaxLayout(
                growSpaceWidthFt.value,
                growSpaceLengthFt.value - plantLengthFt.value * resultsStd.plantsLong,
                plantWidthFt.value,
                plantLengthFt.value
            );
            resultsStd.crackFill = Math.max(crackFillStdWide, crackFillStdLong);

            // Repeat the same process for the swap layout
            // ...
            const resultsSwap = {
                plantsLong: Math.floor(growSpaceLengthFt.value / plantWidthFt.value),
                plantsWide: Math.floor(growSpaceWidthFt.value / plantLengthFt.value),
            };

            const crackFillSwapWide = simpleMaxLayout(
                growSpaceWidthFt.value - plantLengthFt.value * resultsSwap.plantsWide,
                growSpaceLengthFt.value,
                plantWidthFt.value,
                plantLengthFt.value
            );

            const crackFillSwapLong = simpleMaxLayout(
                growSpaceWidthFt.value,
                growSpaceLengthFt.value - plantWidthFt.value * resultsSwap.plantsLong,
                plantWidthFt.value,
                plantLengthFt.value
            );


            resultsSwap.crackFill = Math.max(crackFillSwapWide, crackFillSwapLong);

            // // Choose the optimal result
            // if (resultsStd.plantsLong * resultsStd.plantsWide + resultsStd.crackFill >
            //     resultsSwap.plantsLong * resultsSwap.plantsWide + resultsSwap.crackFill) {
            //     return resultsStd;
            // } else {
            //     return resultsSwap;
            // }

            /* We've compiled our std and swap orientation
            * along with their respective optimal crackfill
            * scenarios; time to prep for returning data.
            */

            // idiot filter; if there's no room return all zero
            if (
                resultsStd.plantsLong * resultsStd.plantsWide == 0 &&
                resultsSwap.plantsLong * resultsSwap.plantsWide == 0
            ) {
                return {
                    plantsLong: 0,
                    plantsWide: 0,
                    crackFill: 0,
                };
            }

            // now choose the optimal result
            if (
                resultsStd.plantsLong * resultsStd.plantsWide + resultsStd.crackFill >
                resultsSwap.plantsLong * resultsSwap.plantsWide + resultsSwap.crackFill
            ) {
                return resultsStd;
            } else if (
                resultsSwap.plantsLong * resultsSwap.plantsWide + resultsSwap.crackFill >
                resultsStd.plantsLong * resultsStd.plantsWide + resultsStd.crackFill
            ) {
                return resultsSwap;
            } else {
                // prioritize minimum crackfilling if they're equal
                if (resultsStd.crackFill < resultsSwap.crackFill) {
                    return resultsStd;
                } else {
                    return resultsSwap;
                }
            }
        });

        const totalPlants = computed(() => {
            return plantLayout.value.plantsLong * plantLayout.value.plantsWide + plantLayout.value.crackFill;
        });

        const totalSpace = computed(() => {
        return growSpaceWidthFt.value * growSpaceLengthFt.value;
        });

        const usedSpace = computed(() => {
        return totalPlants.value * (plantWidthFt.value * plantLengthFt.value);
        });

        const wastedSpace = computed(() => {
        return totalSpace.value - usedSpace.value;
        });

        const spaceEfficiency = computed(() => {
        return round(usedSpace.value / totalSpace.value * 100, 2);
        });
  
        return {
            growSpaceWidthFt,
            growSpaceLengthFt,
            plantWidthFt,
            plantLengthFt,
            plantLayout,
            // Include other computed properties here
            totalPlants,
            totalSpace,
            usedSpace,
            wastedSpace,
            spaceEfficiency,
            ConversionFactors,
            t: translations.t
        };
    },
  };
  </script>
  